import { Observable, ReplaySubject, startWith, Subject, switchMap, tap, timer } from 'rxjs';
import { MapValueType } from 'types/UtilityTypes';

export type CacheOptions = {
  key?: string;
  ttl?: number;
  invalidateKeys?: string[];
};

const $timer = ($invalidator: Subject<any>, ttl: NonNullable<CacheOptions['ttl']>) =>
  $invalidator.pipe(
    startWith(1),
    switchMap(() => timer(0, ttl))
  );

class CacheService {
  private cacheStore = new Map<
    string,
    {
      $invalidator: Subject<any>;
      $emitter: ReplaySubject<any>;
    }
  >();

  public cache = (observable: Observable<unknown>, options: CacheOptions) => {
    // Early return in case of mutations, no need of caching values
    if (!options.key) {
      return observable.pipe(
        tap(() => {
          this.invalidateKeys(options.invalidateKeys);
        })
      );
    }

    const $foundEmitter = this.getCacheEmitter(options.key);
    if ($foundEmitter) {
      return $foundEmitter.asObservable();
    }

    const ttl = options.ttl || 1000 * 60 * 5;

    this.setCacheKey(options.key, {
      $invalidator: new Subject(),
      $emitter: new ReplaySubject(1, ttl),
    });

    $timer(this.getCacheInvalidator(options.key)!, ttl)
      .pipe(
        switchMap(() =>
          observable.pipe(
            tap(() => {
              this.invalidateKeys(options.invalidateKeys);
            })
          )
        )
      )
      .subscribe({
        next: (value) => {
          this.getCacheEmitter(options.key)!.next(value);
        },
        error: (err) => {
          this.getCacheEmitter(options.key)!.error(err);
        },
        complete: () => {
          this.getCacheEmitter(options.key)!.complete();
        },
      });

    return this.getCacheEmitter(options.key)!.asObservable();
  };

  private getCacheEmitter = (key: CacheOptions['key']) => {
    if (key) {
      return this.cacheStore.get(key)?.$emitter;
    }
  };

  private getCacheInvalidator = (key: CacheOptions['key']) => {
    if (key) {
      return this.cacheStore.get(key)?.$invalidator;
    }
  };

  private setCacheKey = (key: CacheOptions['key'], value: MapValueType<typeof this.cacheStore>) => {
    if (key) {
      return this.cacheStore.set(key, value);
    }
  };

  private invalidateKeys = (keys: CacheOptions['invalidateKeys']) => {
    if (keys?.length) {
      [...this.cacheStore.keys()].forEach((k) => {
        if (keys.some((prefix) => k.startsWith(prefix))) {
          this.getCacheInvalidator(k)?.next(1);
        }
      });
    }
  };

  constructor() {
    if (import.meta.env.DEV) {
      (window as any).appCacheStore = this.cacheStore;
    }
  }
}

export const cacheService = new CacheService();
